import React, { useState } from 'react';
import dayjs from 'dayjs';

import Text from '~components/atoms/Text';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';

import { useAppSelector } from '~redux/hooks';
import { FundTypeEnum, GoldTypeEnum, TransactionStatusEnum } from '~utils/enums';

import ReceiptPDF from './ReceiptPDF';
import * as ReceiptStyles from './_goldTransactionDetails.module.scss';
import SummaryPDF from './SummaryPDF';

const GoldTransactionDetails = () => {
    const selectedTransaction = useAppSelector((state) => state.gold.buyAndSellGold.selectedTransaction);
    const discountList = useAppSelector((state) => state.setting.discounts.data);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    if (!selectedTransaction) return null;

    const totalQuantity = (amount: number) => {
        if (typeof amount === 'number') {
            if (selectedTransaction.goldType === GoldTypeEnum.PaperGold) {
                return `${Number(amount).toFixed(3)} g`;
            }

            return `${Number(amount)} pcs`;
        } return '-';
    };

    const handleViewSerialNo = (serialNo: string[]) => {
        setIsSerialNoModalOpen(true);

        if (serialNo.length === 0) return;

        setSerialNoList(serialNo.map((code) => code));
    };

    const renderSerialNo = (serialNo: string[]) => {
        if (serialNo.length === 0) return ': N/A';

        return (
            <a
                className={ReceiptStyles['show-serial-no']}
                onClick={() => handleViewSerialNo(serialNo)}
            >
                : Show all...
            </a>
        );
    };

    // const getDiscountAmount = () => {
    //     if (selectedTransaction.discountRate){
    //         const shifted = ((selectedTransaction.discountRate * 100) / 100);

    //         if (Number.isInteger(shifted * 100)) {
    //             return `RM${shifted.toFixed(2)}`;
    //         }

    //         return `RM${parseFloat(shifted.toFixed(6)).toString()}`;
    //     }

    //     if (selectedTransaction.discountRatePercentage){
    //         const shifted = selectedTransaction.discountRatePercentage * 100;

    //         if (Number.isInteger(shifted * 100)) {
    //             return `${shifted.toFixed(2)}%`;
    //         }

    //         return `${parseFloat(shifted.toFixed(6)).toString()}%`;
    //     }

    //     return '-';
    // };

    const getDiscountName = (discountId: number) => {
        const selectedDiscount = discountList.find(discount => discount.discountId === discountId);

        if (!selectedDiscount) return '-';

        return selectedDiscount.discountName;
    };

    const itemsSummary = selectedTransaction.items.flatMap((item, index) => ([
        { label: `Item ${index + 1}` },
        {
            label: 'Product Name',
            value: item.productName,
        },
        {
            label: 'Discount Name',
            value: item.discountName || '-',
        },
        {
            label: 'Discounted Price',
            value: item.discountedPrice ? `RM${item.discountedPrice}` : '-',
        },
        {
            label: 'Serial No.',
            value: item.serialNo ? renderSerialNo(item.serialNo) : '-',
        },
        {
            label: 'Price Per Gram (MYR)',
            value: item.goldPrice ? `${(Math.round(Number(item.goldPrice) * 100) / 100).toFixed(2)}` : '-',
        },
        {
            label: 'Quantity',
            value: totalQuantity(item.amount),
        },
    ]));

    const summaryTable = [
        {
            label: 'Transaction Status',
            value: TransactionStatusEnum[selectedTransaction.status],
        },
        {
            label: 'Transaction ID',
            value: selectedTransaction.transactionId,
        },
        {
            label: 'Transaction Date',
            value: dayjs(selectedTransaction.createdAt).format('DD/MM/YYYY hh:mm A'),
        },
        {
            label: 'Earmark End Date',
            value: selectedTransaction.earmarkEndDate ? dayjs(selectedTransaction.earmarkEndDate).format('DD/MM/YYYY hh:mm A') : '-',
        },
        {
            label: 'Earmark Transaction',
            value: selectedTransaction.isEarmarkTransaction ? 'Yes' : '-',
        },
        {
            label: 'Project Name',
            value: selectedTransaction.projectName ? selectedTransaction.projectName : '-',
        },
        {
            label: 'Branch Name',
            value: selectedTransaction.branchName,
        },
        {
            label: 'Branch Code',
            value: selectedTransaction.branchCode,
        },
        {
            label: 'Region Name',
            value: selectedTransaction.regionName,
        },
        {
            label: 'Register Category',
            value: `${FundTypeEnum[selectedTransaction.fundType] || '-'} | ${selectedTransaction.goldType ? GoldTypeEnum[selectedTransaction.goldType].split(/(?=[A-Z])/).join(' ') : ''}`,
        },
        {
            label: 'Name of Customer/Company Firm/Association',
            value: selectedTransaction.name,
        },
        {
            label: 'Identification No.',
            value: selectedTransaction.idNo,
        },
        {
            label: 'CIF No.',
            value: selectedTransaction.CIFNo,
        },
        // {
        //     label: 'Discount Name',
        //     value: selectedTransaction?.discountName || '-',
        // },
        // {
        //     label: 'Discount (MYR/%)',
        //     value: getDiscountAmount(), // TODO: later need to calculate
        // },
        // {
        //     label: 'Serial No.',
        //     value: renderSerialNo(),
        // },
        // {
        //     label: 'Price per gram (MYR)',
        //     value: `RM${selectedTransaction.goldPrice}`,
        // },
        // {
        //     label: 'Quantity',
        //     value: totalQuantity(),
        // },
        ...itemsSummary,
        {
            label: 'Sales Personnel Name',
            value: selectedTransaction.salesPersonnelName || '-',
        },
        {
            label: 'Referral Name',
            value: selectedTransaction.referralName || '-',
        },
        {
            label: 'Total Amount (MYR)',
            value: `RM${(Math.round(Number(selectedTransaction.items[0].totalNetValue) * 100) / 100).toFixed(2)}`,
        },
    ];

    const productTableHeader = ['Gold Product', 'Price per gram (MYR)'];
    const productTableData = selectedTransaction.items.map(item => {
        return {
            productName: item.productName,
            goldPrice: (Math.round(Number(item.goldPrice) * 100) / 100).toFixed(2),
        };
    });

    return (
        <div className={ReceiptStyles.container}>
            <table className={ReceiptStyles['details-table']}>
                <tbody>
                    {summaryTable.map((item, index) => {
                        return (
                            <tr key={index}>
                                <td>{item.label}</td>
                                <td>{(item.label === 'Serial No.' ? item.value : `${item.value === undefined ? '' : `: ${item.value}`}`)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className={ReceiptStyles['product-container']}>
                <Text
                    label='Gold Description'
                    isBold
                />

                <table>
                    <tbody>
                        <tr>
                            {productTableHeader.map((item, index) => (
                                <td key={index}>
                                    {item}
                                </td>
                            ))}
                        </tr>

                        {productTableData.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    {item.productName}
                                </td>

                                <td>
                                    {item.goldPrice}
                                </td>
                            </tr>
                        ))}

                        {/* <tr>
                            {productTableData.map((item, index) => (
                                <td key={index}>
                                    {item}
                                </td>
                            ))}
                        </tr> */}
                    </tbody>
                </table>
            </div>

            <div className={ReceiptStyles['button-container']}>
                <SummaryPDF transactionType={selectedTransaction} />

                <ReceiptPDF transactionDetails={selectedTransaction} />
            </div>

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </div>
    );
};

export default GoldTransactionDetails;
